import { ReactNode, useEffect, useState } from 'react';

interface PageTransitionProps {
  children: ReactNode;
  isActive: boolean;
  direction?: 'right-to-left' | 'left-to-right';
}

export default function PageTransition({
  children,
  isActive,
  direction = 'right-to-left'
}: PageTransitionProps) {
  const [shouldRender, setShouldRender] = useState(isActive);

  // Track the animation state separately
  const [isAnimating, setIsAnimating] = useState(false);

  // Calculate positions based on direction
  const enterPosition = 'translate-x-0';
  const initialPosition = direction === 'right-to-left' ? 'translate-x-full' : '-translate-x-full';
  const exitPosition = direction === 'right-to-left' ? '-translate-x-full' : 'translate-x-full';

  // Animation classes based on current state
  const animationClass = isActive
    ? (isAnimating ? enterPosition : initialPosition)
    : exitPosition;

  // Handle mounting/unmounting and animation states
  useEffect(() => {
    if (isActive) {
      // Entering: First render the component
      setShouldRender(true);

      // Then after a small delay, start the animation
      const timer = setTimeout(() => {
        setIsAnimating(true);
      }, 20);

      return () => clearTimeout(timer);
    } else {
      // Exiting: First animate out
      setIsAnimating(false);

      // Then after animation completes, unmount
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 350); // Slightly longer than transition duration to ensure it completes

      return () => clearTimeout(timer);
    }
  }, [isActive]);

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed inset-0 z-30 bg-white transform transition-transform duration-300 ease-out ${animationClass}`}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      {children}
    </div>
  );
}
